import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { canonicalUrl } from "@/lib/structured-data";

export type AccessReportWaitBucket =
  | "under_2w"
  | "w2_6"
  | "w6_12"
  | "over_3m"
  | "still_waiting";

export interface AccessReportPeriod {
  from: string;
  to: string;
}

export interface AccessReportSpecialtyWaitReports {
  n: number;
  buckets: Record<AccessReportWaitBucket, number>;
  outcomes: {
    accepted: number;
    declined: number;
    no_reply: number;
  };
}

export interface AccessReportSpecialty {
  specialty: string;
  listed: number;
  withWebsite: number;
  withPhone: number;
  confirmed: number;
  accepting: number;
  waitReports: AccessReportSpecialtyWaitReports | null;
}

export interface AccessReportSnapshot {
  issueNo: number;
  city: string;
  period: AccessReportPeriod;
  headline: {
    listedSpecialists: number;
    specialties: number;
    suburbs: number;
    liveCoverage: {
      confirmed: number;
      total: number;
      pct: number;
    };
    acceptingNow: number;
    waitReports: {
      received: number;
      usable: number;
    };
  };
  findability: {
    total: number;
    website: number;
    phone: number;
    email: number;
    fax: number;
    referralMethodConfirmed: number;
    telehealthListed: number;
    bulkBillingListed: number;
  };
  bySpecialty: AccessReportSpecialty[];
  bySuburb: Array<{
    suburb: string;
    listed: number;
  }>;
  context: {
    alliedHealthListed: number;
    gpPracticesListed: number;
    briefSubscribers: number;
    members: number;
  };
  defaultFindings: string[];
}

export interface AccessReportNarrative {
  intro: string;
  keyFindings: string[];
  methodology: string;
  notes: string;
  pressLine: string;
}

export interface AccessReportIssueSummary {
  slug: string;
  title: string;
  issueNo: number;
  publishedAt: string;
  period: AccessReportPeriod;
  headline: AccessReportSnapshot["headline"];
}

export interface AccessReportIssue {
  slug: string;
  title: string;
  issueNo: number;
  publishedAt: string;
  period: AccessReportPeriod;
  data: AccessReportSnapshot;
  narrative: AccessReportNarrative;
}

export interface AdminAccessReportIssueSummary {
  id: string;
  slug: string;
  title: string;
  issueNo: number;
  status: "draft" | "published";
  computedAt: string;
  publishedAt: string | null;
}

export interface AdminAccessReportIssue
  extends Omit<AccessReportIssue, "publishedAt">,
    AdminAccessReportIssueSummary {
  publishedAt: string | null;
}

export function useAccessReportIssues(citySlug: string) {
  return useQuery({
    queryKey: ["access-report", citySlug],
    queryFn: () =>
      api.get<{ issues: AccessReportIssueSummary[] }>(
        `/v1/public/${citySlug}/access-report`,
        { skipAuth: true },
      ),
    enabled: Boolean(citySlug),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useAccessReportIssue(citySlug: string, slug: string) {
  return useQuery({
    queryKey: ["access-report", citySlug, slug],
    queryFn: () =>
      api.get<{ issue: AccessReportIssue }>(
        `/v1/public/${citySlug}/access-report/${encodeURIComponent(slug)}`,
        { skipAuth: true },
      ),
    enabled: Boolean(citySlug && slug),
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useAdminAccessReportIssues() {
  return useQuery({
    queryKey: ["admin-access-report"],
    queryFn: () =>
      api.get<{ issues: AdminAccessReportIssueSummary[] }>(
        "/v1/admin/access-report",
      ),
    retry: false,
  });
}

export function useAdminAccessReportIssue(id: string) {
  return useQuery({
    queryKey: ["admin-access-report", id],
    queryFn: () =>
      api.get<{ issue: AdminAccessReportIssue }>(
        `/v1/admin/access-report/${encodeURIComponent(id)}`,
      ),
    enabled: Boolean(id),
    retry: false,
  });
}

export function useComputeAccessReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      api.post<{ issue: AdminAccessReportIssue }>(
        "/v1/admin/access-report/compute",
      ),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: ["admin-access-report"] }),
  });
}

export function useUpdateAccessReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      title,
      narrative,
    }: {
      id: string;
      title: string;
      narrative: AccessReportNarrative;
    }) =>
      api.patch<{ ok: true }>(
        `/v1/admin/access-report/${encodeURIComponent(id)}`,
        { title, narrative },
      ),
    onSuccess: (_response, variables) => {
      void queryClient.invalidateQueries({ queryKey: ["admin-access-report"] });
      void queryClient.invalidateQueries({
        queryKey: ["admin-access-report", variables.id],
      });
    },
  });
}

export function usePublishAccessReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ ok: true; slug: string }>(
        `/v1/admin/access-report/${encodeURIComponent(id)}/publish`,
      ),
    onSuccess: (_response, id) => {
      void queryClient.invalidateQueries({ queryKey: ["admin-access-report"] });
      void queryClient.invalidateQueries({
        queryKey: ["admin-access-report", id],
      });
    },
  });
}

const CSV_HEADERS = [
  "Specialty",
  "Listed",
  "Website",
  "Phone",
  "Confirmed",
  "Accepting",
  "Wait reports",
] as const;

export function toCsv(bySpecialty: AccessReportSpecialty[]): string {
  const rows = bySpecialty.map((row) => [
    row.specialty,
    row.listed,
    row.withWebsite,
    row.withPhone,
    row.confirmed,
    row.accepting,
    row.waitReports?.n ?? "",
  ]);

  return [CSV_HEADERS, ...rows]
    .map((row) => row.map(csvCell).join(","))
    .join("\n");
}

export function formatPercentage(value: number): string {
  if (!Number.isFinite(value)) return "0%";
  return `${Math.round(value * 100)}%`;
}

export function buildCitation(
  issue: Pick<AccessReportIssue, "issueNo" | "slug" | "period">,
): string {
  const reportDate = parseDateOnly(issue.period.to);
  const year = reportDate?.getFullYear() ?? Number(issue.period.to.slice(0, 4));
  const monthYear = reportDate
    ? new Intl.DateTimeFormat("en-AU", {
        month: "long",
        year: "numeric",
      }).format(reportDate)
    : issue.period.to;

  return `Data © PulseBoard Canberra, published under CC BY 4.0. Cite as: PulseBoard Canberra (${year}). Specialist Access Report No.${issue.issueNo}, ${monthYear}. ${canonicalUrl(`/access-report/${issue.slug}`)}`;
}

export function formatReportDate(value: string, short = false): string {
  const date = /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? parseDateOnly(value)
    : new Date(value);
  if (!date || Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat("en-AU", {
    day: "numeric",
    month: short ? "short" : "long",
    year: "numeric",
    timeZone: "Australia/Sydney",
  }).format(date);
}

function parseDateOnly(value: string): Date | null {
  const [year, month, day] = value.split("-").map(Number);
  if (!year || !month || !day) return null;
  const date = new Date(year, month - 1, day);
  return Number.isNaN(date.getTime()) ? null : date;
}

function csvCell(value: string | number): string {
  const text = String(value);
  return /[",\r\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
}
